/**
 * LaunchActivationPage — route /project/:projectId/launch.
 * The ONLY router-aware file in the module (DC-9): reads params/location, injects
 * onNavigate, and runs the questionnaire-return handler:
 *   params present → wait for GET 200 (membership proven by requireProjectAuth, DC-24)
 *   → POST intent once → strip the params with history.replace (AC-008: reload/Back never replay).
 *   403/404/other failure or a tampered value → strip only, write nothing (AC-058).
 */
import React, { useEffect, useRef } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { ActivationChecklist } from '../components/ActivationChecklist';
import { useActivationDialogHost } from '../components/ActivationDialogHost';
import { useLaunchActivation } from '../hooks/useLaunchActivation';
import { parseIntentParams, stripIntentParams } from '../utils/intentParams';
import { getQuestionnaireUrl } from '../utils/questionnaireEnv';

export const LaunchActivationPage: React.FC = () => {
  const { projectId } = useParams<{ projectId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { data, loading, error, status, refetch, setIntent } = useLaunchActivation(projectId);
  const handledSearch = useRef<string | null>(null);
  const questionnaireUrl = getQuestionnaireUrl();
  // Class-D dialogs open IN PLACE on this page (contracts §ACTION_ROUTING, follow-up F1):
  // the host owns `domainDrawer` + `addIntegration`; any other kind returns false from
  // `openDialog` and `runActivationAction` falls back to the item's `fallbackRoute`.
  const { openDialog, host } = useActivationDialogHost(projectId, { onSaved: refetch });

  useEffect(() => {
    if (!projectId || loading) return;
    const parsed = parseIntentParams(location.search);
    if (!parsed.present || handledSearch.current === location.search) return;
    handledSearch.current = location.search;
    const strip = () => navigate({ pathname: location.pathname, search: stripIntentParams(location.search) }, { replace: true });
    const isMember = Boolean(data) && status === undefined;
    if (!isMember || !parsed.intent) { strip(); return; }
    void setIntent(parsed.intent).catch(() => undefined).finally(strip);
  }, [projectId, loading, data, status, location.pathname, location.search, navigate, setIntent]);

  if (!projectId) return null;
  return (
    <>
      <ActivationChecklist
        projectId={projectId}
        variant="full"
        onNavigate={(path) => navigate(path)}
        openDialog={openDialog}
        questionnaireUrl={questionnaireUrl}
        activation={{ data, loading, error, status, refetch, setIntent }}
      />
      {host}
    </>
  );
};

export default LaunchActivationPage;
